import React, { useEffect, useState } from "react";
import CustomModal from "../../components/CustomModal";
import { Catalog, LANGUAGE_APPS, LanguageApp } from "./api";
import { useSaveLanguage } from "./hooks";
import { useT } from "../../i18n";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Set to update an existing language's files; omit to add a new language. */
  language?: { code: string; name: string };
}

type Picked = { file: string; catalog?: Catalog; error?: boolean };

const APP_LABEL: Record<LanguageApp, string> = {
  admin: "language.apps.admin",
  partner: "language.apps.partner",
};

const inputClass =
  "w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-gray-800 disabled:bg-gray-100 disabled:text-gray-500";
const labelClass = "block text-sm text-gray-700 mb-1";

const AddLanguageModal: React.FC<Props> = ({ open, onClose, language }) => {
  const t = useT();
  const isNew = !language;
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [files, setFiles] = useState<Partial<Record<LanguageApp, Picked>>>({});
  const saveLanguage = useSaveLanguage();

  useEffect(() => {
    if (!open) return;
    setCode(language?.code ?? "");
    setName(language?.name ?? "");
    setFiles({});
  }, [open, language?.code, language?.name]);

  const pick = async (app: LanguageApp, file?: File) => {
    if (!file) return setFiles(({ [app]: _, ...rest }) => rest);
    let picked: Picked;
    try {
      picked = { file: file.name, catalog: JSON.parse(await file.text()) };
    } catch {
      picked = { file: file.name, error: true };
    }
    setFiles((f) => ({ ...f, [app]: picked }));
  };

  const hasError = Object.values(files).some((f) => f?.error);
  const catalogs = Object.fromEntries(
    Object.entries(files).map(([app, f]) => [app, f!.catalog])
  ) as Partial<Record<LanguageApp, Catalog>>;
  const canSubmit = !hasError && (isNew || Object.keys(catalogs).length > 0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    saveLanguage.mutate(
      { code: code.trim(), name: name.trim(), isNew, catalogs },
      { onSuccess: onClose }
    );
  };

  return (
    <CustomModal open={open} onClose={onClose} className="max-w-lg">
      <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
        <div className="flex items-center justify-center w-10 h-10 shrink-0 rounded-lg bg-gray-100">
          <i className={`fa-solid ${isNew ? "fa-language" : "fa-file-pen"} text-xl text-gray-800`}></i>
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            {isNew
              ? t("language.addLanguage")
              : t("language.modal.editTitle", { name: language.name })}
          </h2>
          <div className="text-sm text-gray-400 font-medium leading-tight">
            {isNew ? t("language.modal.intro") : t("language.modal.editIntro")}
          </div>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="pt-6 space-y-5">
        <div className="grid grid-cols-[7rem_1fr] gap-3">
          <div>
            <label className={labelClass}>
              {t("language.modal.codeLabel")} <span className="text-red-500">*</span>
            </label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              disabled={!isNew}
              pattern="[a-z]{2}(-[A-Z]{2})?"
              title={t("language.modal.codeHint")}
              placeholder={t("language.modal.codePlaceholder")}
              className={`${inputClass} font-mono`}
            />
          </div>
          <div>
            <label className={labelClass}>
              {t("language.modal.nameLabel")} <span className="text-red-500">*</span>
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={!isNew}
              placeholder={t("language.modal.namePlaceholder")}
              className={inputClass}
            />
          </div>
        </div>

        {LANGUAGE_APPS.map((app) => {
          const picked = files[app];
          return (
            <div key={app}>
              <span className={labelClass}>
                {t(APP_LABEL[app])}
              </span>
              <div className="flex items-center gap-3 min-w-0">
                <label className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-gray-800">
                  <i className="fa-solid fa-upload text-gray-500"></i>
                  {t("language.modal.uploadFile")}
                  <input
                    type="file"
                    accept="application/json,.json"
                    className="sr-only"
                    onChange={(e) => {
                      pick(app, e.target.files?.[0]);
                      e.target.value = ""; // allow re-picking the same file
                    }}
                  />
                </label>
                {picked && (
                  <span
                    className={`min-w-0 inline-flex items-center gap-2 pl-3 pr-1 py-1 rounded-lg text-sm ${
                      picked.error ? "bg-red-50 text-red-700" : "bg-green-50 text-green-800"
                    }`}
                    title={picked.error ? t("language.toast.invalidJson") : picked.file}
                  >
                    <i
                      className={`fa-solid ${
                        picked.error ? "fa-triangle-exclamation" : "fa-file-circle-check"
                      }`}
                    ></i>
                    <span className="truncate">{picked.file}</span>
                    <button
                      type="button"
                      onClick={() => pick(app)}
                      className="h-6 w-6 shrink-0 rounded-md opacity-60 hover:opacity-100 hover:bg-white/70"
                      aria-label={t("common.cancel")}
                    >
                      <i className="fa-solid fa-xmark text-xs"></i>
                    </button>
                  </span>
                )}
              </div>
              {picked?.error && (
                <p className="mt-1 text-xs text-red-600">{t("language.toast.invalidJson")}</p>
              )}
            </div>
          );
        })}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saveLanguage.isPending}
            className="px-6 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 font-medium"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={saveLanguage.isPending || !canSubmit}
            className="px-6 py-2 rounded-lg bg-[#111827] text-white hover:bg-gray-800 font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {saveLanguage.isPending ? (
              <>
                <i className="fa-solid fa-spinner fa-spin"></i>
                {t("common.loading.saving")}
              </>
            ) : (
              <>
                <i className={`fa-solid ${isNew ? "fa-plus" : "fa-floppy-disk"}`}></i>
                {isNew ? t("language.addLanguage") : t("language.modal.save")}
              </>
            )}
          </button>
        </div>
      </form>
    </CustomModal>
  );
};

export default AddLanguageModal;
